import { Col, Row, Select, Card, Button, Calendar, Tree, Spin } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import "./styles.css";
import { postApis } from "../Common/apis";
import {
  LeftOutlined,
  RightOutlined,
  CloudDownloadOutlined,
} from "@ant-design/icons";
import { exportDataMatrixUrl, exportRequestUrl } from "../Common/endpoints";
import DateTimePicker from "../Common/DateTimePicker";
import dayjs from "dayjs";
import Download_icon from "../../assets/images/icons/download_icon.svg";
import { ColumnFields, IMU } from "../Common/DropDownData";
import { toast } from "react-toastify";

const columsName = Object.keys(ColumnFields);
const allColumns = [
  {
    title: "All",
    key: "ALL",
    children: columsName.map((data) => ColumnFields[data]),
  },
];

const columns = {};
for (const [key, value] of Object.entries(ColumnFields)) {
  columns[key] = value.children.map((child) => child.key);
}
const IMUColumns = IMU.children.map((child) => child.key);

const Filter = ({ vehicle, getDownloadHistoryData }) => {
  const authToken = localStorage.getItem("authToken");
  const [filterValue, setFilterValue] = useState({ columnList: ["ALL"] });
  const [timeRange, setTimeRange] = useState([
    dayjs().set("date", 1).set("hour", 0).set("minute", 0).set("second", 0),
    dayjs(),
  ]);
  const [availableDates, setAvailableDates] = useState([]);
  const [dataMatrixLoading, setDataMatrixLoading] = useState(false);
  const [downloader, setDownloader] = useState(false);

  const getExportMatrix = async () => {
    setDataMatrixLoading(true);
    let selectedColumnNameList = [];
    let selectedColumns = {};
    let imuValues = [];

    if (filterValue?.columnList?.includes("ALL")) {
      selectedColumnNameList = columsName;
      selectedColumns = { ...columns };
    } else {
      selectedColumnNameList = filterValue?.columnList;
      filterValue?.checkedKeys?.forEach((data) => {
        selectedColumnNameList.forEach((column, index) => {
          if (columns[column].includes(data)) {
            selectedColumns[column] = [
              ...(selectedColumns?.[column] || []),
              data,
            ];
          }
        });
      });
    }

    imuValues = selectedColumns?.TELEMETRY?.filter((item) =>
      IMUColumns.includes(item)
    );
    selectedColumns["TELEMETRY"] = selectedColumns?.TELEMETRY?.filter(
      (item) => !IMUColumns.includes(item)
    );

    if (imuValues?.length > 0) {
      selectedColumns.IMU = imuValues;
      selectedColumnNameList = [...selectedColumnNameList, "IMU"];
    }

    const body = {
      imei: vehicle?.vehImei,
      yearMonth: dayjs(filterValue?.calendarValue).format("YYYY-MM"),
      exportDataTypes: selectedColumnNameList,
      rawFlag: filterValue?.rawFlag,
      columnsLists: Object.values(selectedColumns),
    };

    await postApis(exportDataMatrixUrl, body, authToken)
      .then((res) => {
        setDataMatrixLoading(false);
        if (res?.data) {
          setAvailableDates(res?.data);
        }
      })
      .catch((e) => {
        setDataMatrixLoading(false);
      });
  };

  useEffect(() => {
    if (vehicle?.vehId && vehicle?.vehId !== "") {
      setFilterValue((prev) => ({
        ...prev,
        calendarValue: dayjs(),
        columnList: ["ALL"],
        checkedKeys: ["ALL"],
        rawFlag: false,
      }));
    }

    // eslint-disable-next-line
  }, [vehicle?.vehId, authToken]);

  useMemo(() => {
    if (vehicle?.vehId && vehicle?.vehId !== "") {
      getExportMatrix();
    }

    // eslint-disable-next-line
  }, [filterValue]);

  const onMonthChange = (value) => {
    setFilterValue((prev) => ({
      ...prev,
      calendarValue: dayjs(prev?.calendarValue).add(value, "M"),
    }));
  };

  const onHeaderRender = ({ value }) => {
    let current = value?.clone();
    const localeData = value?.localeData();
    let month = localeData?.months(current);
    let year = value?.year();
    return (
      <div className="calendar-header">
        <span>
          <LeftOutlined
            className="fs1015 ml1"
            onClick={() => onMonthChange(-1)}
          />
        </span>
        <span className="flex-center">{month + " " + year}</span>
        <span>
          <RightOutlined
            className="fs1015 mr1"
            onClick={() => onMonthChange(1)}
          />
        </span>
      </div>
    );
  };

  const onChangePicker = (value) => {
    setTimeRange(value);
    if (
      dayjs(filterValue?.calendarValue).month() !== dayjs(value?.[0]).month()
    ) {
      setFilterValue((prev) => ({ ...prev, calendarValue: value?.[0] }));
    }
  };

  const cellRender = (date, info) => {
    let startDate = null;
    let endDate = null;
    if (timeRange?.length > 1) {
      startDate = timeRange[0];
      endDate = timeRange[1];
    }

    if (info.type === "date") {
      const dateType = startDate?.isSame(date, "date")
        ? "start-date"
        : endDate?.isSame(date, "date")
        ? "end-date"
        : availableDates?.includes(dayjs(date).format("YYYY-MM-DD"))
        ? "range-date"
        : null;

      return React.cloneElement(info.originNode, {
        ...info.originNode.props,
        className: "calender-box",
        children: (
          <div className={"flex-center " + dateType}>
            <div className="date-lable">{date.get("date")}</div>
            {availableDates?.includes(dayjs(date).format("YYYY-MM-DD")) ? (
              <div
                className="download-icon"
                onClick={
                  dateType
                    ? (e) =>
                        exportCSVFile(
                          e,
                          dayjs(date)
                            .set("hour", 0)
                            .set("minute", 0)
                            .set("second", 0),
                          dayjs(date).endOf("day")
                        )
                    : null
                }
              >
                <img src={Download_icon} alt="export" />
              </div>
            ) : null}
          </div>
        ),
      });
    }
  };

  const onCheck = (checkedKeysValue) => {
    setFilterValue((prev) => ({ ...prev, checkedKeys: checkedKeysValue }));
  };

  const onColumnList = (value) => {
    const [firstColumn, ...otherColumns] = value;
    const selectedColumn =
      !value?.length || otherColumns.at(-1) === "ALL"
        ? ["ALL"]
        : firstColumn === "ALL"
        ? otherColumns
        : value;
    setFilterValue((prev) => ({
      ...prev,
      checkedKeys: [
        ...(selectedColumn.includes("ALL") ? columsName : selectedColumn).map(
          (column) => columns[column]
        ),
      ].flat(),
      columnList: selectedColumn,
    }));
  };

  // const onRawFlag = (event) => {
  //   setFilterValue((prev) => ({ ...prev, rawFlag: event.target.value }));
  // };

  const exportCSVFile = async (
    e,
    startTime = timeRange?.[0],
    endTime = timeRange?.[1]
  ) => {
    if (availableDates?.length > 0) {
      setDownloader(true);
      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      const currentOrganisation = userDetails?.currentOrganisation;

      let selectedColumnNameList = [];
      let selectedColumns = {};
      let imuValues = [];

      if (filterValue?.columnList?.includes("ALL")) {
        selectedColumnNameList = columsName;
        selectedColumns = { ...columns };
      } else {
        selectedColumnNameList = filterValue?.columnList;
        filterValue?.checkedKeys?.forEach((data) => {
          selectedColumnNameList.forEach((column, index) => {
            if (columns[column].includes(data)) {
              selectedColumns[column] = [
                ...(selectedColumns?.[column] || []),
                data,
              ];
            }
          });
        });
      }

      imuValues = selectedColumns?.TELEMETRY?.filter((item) =>
        IMUColumns.includes(item)
      );
      selectedColumns["TELEMETRY"] = selectedColumns?.TELEMETRY?.filter(
        (item) => !IMUColumns.includes(item)
      );

      if (imuValues?.length > 0) {
        selectedColumns.IMU = imuValues;
        selectedColumnNameList = [...selectedColumnNameList, "IMU"];
      }

      const body = {
        imei: vehicle?.vehImei,
        orgId: currentOrganisation?.id,
        columnLists: Object.values(selectedColumns),
        exportDataTypes: selectedColumnNameList,
        rawFlag: filterValue?.rawFlag,
        startTime: Math.floor(startTime),
        endTime: Math.floor(endTime),
      };
      await postApis(exportRequestUrl, body, authToken)
        .then((res) => {
          setDownloader(false);
          if (res?.data) {
            getDownloadHistoryData();
            toast.success("Processing your request.");
          }
        })
        .catch((e) => {
          toast.error(e.response?.data?.message);
          setDownloader(false);
        });
    } else {
      toast.error(`Data not available for filtering dates and columns.`);
    }
  };

  return (
    <>
      <Col
        xs={24}
        sm={12}
        md={12}
        lg={12}
        xl={5}
        data-testid="column-tree-view"
      >
        <Row>
          <Col className="w100">
            <Select
              data-testid="select-column-list"
              placeholder="Select a columns"
              mode="multiple"
              size="large"
              allowClear
              value={filterValue?.columnList}
              maxTagCount={"responsive"}
              style={{ width: "100%", maxHeight: "40px" }}
              onChange={onColumnList}
            >
              <Select.Option value="ALL">All</Select.Option>
              {columsName?.map((column, index) => (
                <Select.Option key={index} value={column}>
                  {column.charAt(0) + column.slice(1).toLowerCase()}
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row gutter={[16, 16]} className="mt1 h100">
          <Col span={24}>
            <Card className="columns-list">
              <div className="flex-center filter-title">Columns List</div>
              <div className="ml1 selection-box">
                <Tree
                  checkable
                  onCheck={onCheck}
                  checkedKeys={filterValue?.checkedKeys}
                  disabled={filterValue?.columnList?.includes("ALL")}
                  multiple={true}
                  treeData={
                    filterValue?.columnList?.includes("ALL")
                      ? allColumns
                      : filterValue?.columnList?.map(
                          (column) => ColumnFields[column]
                        )
                  }
                  defaultExpandAll={true}
                />
              </div>
            </Card>
          </Col>
        </Row>
      </Col>

      <Col
        xs={24}
        sm={24}
        md={24}
        lg={24}
        xl={14}
        xxl={14}
        data-testid="calendar-view"
      >
        <Row
          gutter={[16, 16]}
          className="pt2"
          align={"middle"}
          justify={"space-between"}
        >
          <Col xs={24} sm={15} md={10} lg={10} xl={9} xxl={8}>
            <DateTimePicker
              dates={timeRange || []}
              featureDate={true}
              onChangePicker={onChangePicker}
              notDefaultValue={true}
            />
          </Col>
          {/* <Col
            xs={16}
            sm={{ span: 16, offset: 0 }}
            md={{ span: 9, offset: 0 }}
            lg={{ span: 8, offset: 2 }}
            xl={{ span: 10, offset: 0 }}
            xxl={{ span: 8, offset: 4 }}
            className="flex-end"
          >
            <Radio.Group
              onChange={onRawFlag}
              value={filterValue?.rawFlag}
              className="raw-data-flag"
            >
              <Radio value={true}>Raw Data</Radio>
              <Radio value={false}>Cleaned Data</Radio>
            </Radio.Group>
          </Col> */}
          <Col xs={8} sm={8} md={5} lg={4} xl={5} xxl={4}>
            <Button
              type="primary"
              className="dwl-btn"
              onClick={exportCSVFile}
              icon={<CloudDownloadOutlined style={{ fontSize: "24px" }} />}
              loading={downloader}
            >
              Request
            </Button>
          </Col>
        </Row>
        <Row className="calendar mt1">
          <Col span={24} className="h100">
            <Spin
              className="fs1"
              spinning={dataMatrixLoading}
              tip="Checking..."
              size="large"
            >
              <Card className="h100">
                <Calendar
                  className="export-calendar"
                  fullCellRender={cellRender}
                  headerRender={onHeaderRender}
                  fullscreen={false}
                  value={filterValue?.calendarValue || null}
                />
              </Card>
            </Spin>
          </Col>
        </Row>
      </Col>
    </>
  );
};

export default Filter;
